/**
 * Credit Context
 * Shared credit state across all components.
 * Replaces standalone useCredits hook instances so that
 * purchasing credits in a modal instantly updates the widget, etc.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useToast } from '@chakra-ui/react';
import { useAuth } from '../hooks/useAuth';
import {
  getCredits,
  purchaseCredits as purchaseCreditsAPI,
  getCreditPackages
} from '../services/featuredTracksService';

const CreditContext = createContext();

export const useCreditContext = () => {
  const context = useContext(CreditContext);
  if (!context) {
    throw new Error('useCreditContext must be used within a CreditProvider');
  }
  return context;
};

export const CreditProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [credits, setCredits] = useState(null);
  const [packages, setPackages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  // Fetch credit balance
  const fetchCredits = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getCredits();
      setCredits(data);
    } catch (err) {
      console.error('Error fetching credits:', err);
      setError(err.response?.data?.detail || 'Failed to fetch credits');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch credit packages
  const fetchPackages = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await getCreditPackages();
      setPackages(data);
    } catch (err) {
      console.error('Error fetching packages:', err);
    }
  }, [isAuthenticated]);

  // Purchase credits
  const purchaseCredits = useCallback(async (amount) => {
    try {
      setLoading(true);
      setError(null);
      const result = await purchaseCreditsAPI(amount);

      // Refresh credits after purchase — updates ALL consumers
      await fetchCredits();

      toast({
        title: 'Credits Purchased',
        description: `Successfully purchased ${result.credits_purchased} credits for $${result.cost}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      return result;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to purchase credits';
      setError(errorMsg);

      toast({
        title: 'Purchase Failed',
        description: errorMsg,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });

      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchCredits, toast]);

  // Initialize on auth
  useEffect(() => {
    if (isAuthenticated) {
      fetchCredits();
      fetchPackages();
    } else {
      setCredits(null);
      setPackages(null);
    }
  }, [isAuthenticated, fetchCredits, fetchPackages]);

  const value = useMemo(() => ({
    credits,
    packages,
    loading,
    error,
    refetch: fetchCredits,
    purchaseCredits
  }), [credits, packages, loading, error, fetchCredits, purchaseCredits]);

  return (
    <CreditContext.Provider value={value}>
      {children}
    </CreditContext.Provider>
  );
};
